/**
 * Toast notification component.
 * 
 * Displays temporary notifications that auto-dismiss.
 * Used for real-time alert notifications.
 */

import { useEffect, useState } from 'react'

export interface ToastData {
  id: string
  type: 'info' | 'success' | 'warning' | 'error' | 'alert'
  title: string
  message?: string
  duration?: number
  icon?: string
}

interface ToastProps {
  toast: ToastData
  onDismiss: (id: string) => void
}

function Toast({ toast, onDismiss }: ToastProps) {
  const [isExiting, setIsExiting] = useState(false)

  useEffect(() => {
    const duration = toast.duration || 5000
    const timer = setTimeout(() => {
      setIsExiting(true)
      setTimeout(() => onDismiss(toast.id), 300) // Wait for exit animation
    }, duration)

    return () => clearTimeout(timer)
  }, [toast, onDismiss])

  const handleDismiss = () => {
    setIsExiting(true)
    setTimeout(() => onDismiss(toast.id), 300)
  }

  const bgColor = {
    info: 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800',
    success: 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800',
    warning: 'bg-yellow-50 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-800',
    error: 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800',
    alert: 'bg-purple-50 dark:bg-purple-900/30 border-purple-200 dark:border-purple-800',
  }[toast.type]

  const textColor = {
    info: 'text-blue-800 dark:text-blue-200',
    success: 'text-green-800 dark:text-green-200',
    warning: 'text-yellow-800 dark:text-yellow-200',
    error: 'text-red-800 dark:text-red-200',
    alert: 'text-purple-800 dark:text-purple-200',
  }[toast.type]

  return (
    <div
      className={`
        ${bgColor} ${textColor}
        border rounded-lg shadow-lg p-4 max-w-sm
        transform transition-all duration-300 ease-out
        ${isExiting ? 'opacity-0 translate-x-full' : 'opacity-100 translate-x-0'}
      `}
      role="alert"
    >
      <div className="flex gap-3">
        {toast.icon && (
          <span className="text-xl flex-shrink-0">{toast.icon}</span>
        )}
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">{toast.title}</p>
          {toast.message && (
            <p className="text-xs opacity-80 mt-0.5 line-clamp-2">{toast.message}</p>
          )}
        </div>
        <button
          onClick={handleDismiss}
          className="text-current opacity-50 hover:opacity-100 transition-opacity"
          aria-label="Dismiss"
        >
          ✕
        </button>
      </div>
    </div>
  )
}

interface ToastContainerProps {
  toasts: ToastData[]
  onDismiss: (id: string) => void
}

export function ToastContainer({ toasts, onDismiss }: ToastContainerProps) {
  if (toasts.length === 0) return null

  return (
    <div 
      className="fixed top-4 right-4 z-50 flex flex-col gap-2"
      aria-live="polite"
      aria-label="Notifications"
    >
      {toasts.map(toast => (
        <Toast key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  )
}

export default Toast
